'use client';

import * as React from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle,} from '@headlessui/react';

// @ts-ignore
import clsx from 'clsx';
import Button from "./Button";

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';

export type ModalProps = {
    open: boolean;
    onClose: (open: boolean) => void;
    title?: React.ReactNode;
    description?: React.ReactNode;
    icon?: React.ReactNode;

    /** Disable closing (esc/backdrop) while true, e.g. during save */
    submitting?: boolean;

    /** Width presets */
    size?: ModalSize;

    /** Custom content (form/anything) */
    children?: React.ReactNode;

    /** If provided, replaces the default footer */
    footer?: React.ReactNode;

    /** Default footer buttons (used when `footer` not provided) */
    primaryText?: string;
    secondaryText?: string;

    onPrimary?: (e: React.MouseEvent | React.FormEvent) => void | Promise<void>;
    onSecondary?: (e: React.MouseEvent) => void;

    /** When using as a <form>, provide onSubmit and set `as="form"`  */
    as?: 'div' | 'form';
    onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void | Promise<void>;

    /** Extra classes when you need custom styling  */
    panelClassName?: string;
};

const sizeMap: Record<ModalSize, string> = {
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-md',
    lg: 'w-full max-w-lg',
    xl: 'w-full max-w-xl',
    xxl: 'w-full max-w-2xl',
    full: 'w-full  max-w-7xl',
};

export default function Modal({
                                  open,
                                  onClose,
                                  title,
                                  description,
                                  icon,
                                  submitting = false,
                                  size = 'md',
                                  children,
                                  footer,
                                  primaryText = 'Save',
                                  secondaryText = 'Cancel',
                                  onPrimary,
                                  onSecondary,
                                  as = 'div',
                                  onSubmit,
                                  panelClassName,
                              }: ModalProps) {

    // Prevent closing when submitting
    const handleClose = (next: boolean) => {
        if (submitting) return;
        onClose(next);
    };

    const panel = (
        <DialogPanel
            transition
            className={clsx(
                'relative transform overflow-y-auto max-h-[calc(100vh-2rem)] rounded-lg bg-white text-left shadow-xl transition-all',
                'data-closed:opacity-0 data-closed:scale-95 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in',
                'p-6 w-full',
                panelClassName,
                sizeMap[size],
            )}
        >
            {(icon || title || description) && (
                <div className="text-center">
                    {icon && (
                        <div
                            className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-gray-100">
                            {icon}
                        </div>
                    )}
                    {title && (
                        <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                            {title}
                        </DialogTitle>
                    )}
                    {description && <p className="mt-2 text-sm text-gray-500">{description}</p>}
                </div>
            )}

            {/* Body */}
            {children && <div className="mt-4">{children}</div>}

            {/* Footer */}
            <div className="mt-6">
                {footer ?? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Button
                            variant="tertiary"
                            size="md"
                            disabled={submitting}
                            onClick={(e) => {
                                if (submitting) return;
                                if (onSecondary) onSecondary(e);
                                else handleClose(false);
                            }}
                        >
                            {secondaryText}
                        </Button>
                        <Button
                            variant="primary"
                            type={as === 'form' ? 'submit' : 'button'}
                            size="md"
                            disabled={submitting}
                            aria-busy={submitting || undefined}
                            onClick={as === 'form' ? undefined : onPrimary}
                        >
                            {submitting ? 'Saving…' : primaryText}
                        </Button>
                    </div>
                )}
            </div>
        </DialogPanel>
    );

    const Wrapper = as === 'form' ? 'form' : React.Fragment;
    const wrapperProps =
        as === 'form'
            ? ({
                onSubmit: async (e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    if (onSubmit) await onSubmit(e);
                },
            } as any)
            : {};

    return (
        <Dialog open={open} onClose={handleClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-900/50 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
            />
            <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                    {as === 'form' ? <Wrapper {...wrapperProps}>{panel}</Wrapper> : panel}
                </div>
            </div>
        </Dialog>
    );
}